import React, { useContext, useEffect, useRef, useState } from "react";
import intl from "react-intl-universal";

import { Plot } from "../../../../script/plot";
import plotUtils from "../../../../script/utils/plotUtils";
import antennaUtils from "../../../../script/utils/antennaUtils";
import operatorUtils from "../../../../script/utils/operatorUtils";
import entryRest from "../../../../rest/entryRest";
import { Progress } from "../../../components/Progress";
import { DialogFooter } from "../../../containers/DialogFooter";
import { EntryContext } from "../../../contexts/EntryContext";
import { DialogBody } from "../../../containers/DialogBody";
import { Dialog } from "../../../containers/Dialog";
import { Spacer } from "../../../components/Spacer";
import { Button } from "../../../components/Button";
import { mdiClose, mdiDownload } from "@mdi/js";
import wattwaechterRest from "../../../../rest/wattwaechterRest";
import { ResultViewType } from "../plan/layers/ResultViewType";
import fieldArrayResultUtils from "../../../../script/utils/fieldArrayResultUtils";
import calcAreaUtils from "../../../../script/utils/calcAreaUtils";
import worstcaseRest from "../../../../rest/worstcaseRest";
import reportRest from "../../../../rest/reportRest";
import { SelectSingle } from "../../../components/SelectSingle";
import { useInput } from "../../../hooks/useInput";
import { SelectMultiple } from "../../../components/SelectMultiple";
import { ReportAreaItem } from "./ReportAreaItem";
import commonUtils from "../../../../script/utils/commonUtils";
import { entryCanvasId, setHiddenEntryCanvasSize } from "../../Entry";
import { InputTextArea } from "../../../components/InputTextArea";
import { Checkbox } from "../../../components/Checkbox";

const layers = Object.freeze({
    plan: "plan",
    areas: "areas",
    result: "result",
    antennas: "antennas",
});


const Evaluation = Object.freeze({
    positive: "POSITIVE",
    negative: "NEGATIVE",
    changeRequired: "CHANGE_REQUIRED",
});


const PlanDefect = Object.freeze({
    noRefPointPlan: "NO_REF_POINT_PLAN",
    noRefPointView: "NO_REF_POINT_VIEW",
    wrongScalePlan: "WRONG_SCALE_PLAN",
    wrongScaleView: "WRONG_SCALE_VIEW",
    wrongDirectionPlan: "WRONG_DIRECTION_PLAN",
    wrongDirectionView: "WRONG_DIRECTION_VIEW",
    wrongControlAreaBoundaries: "WRONG_CONTROL_AREA_BOUNDARIES",
    wrongControlAreaBoundariesSideview: "WRONG_CONTROL_AREA_BOUNDARIES_SIDEVIEW",
    missingNotationReinforcementControlAreaSideview: "MISSING_NOTATION_REINFORCEMENT_CONTROL_AREA_SIDEVIEW"
});


export const ReportDialog = props => {
    const entryContext = useContext(EntryContext);
    const { entry } = entryContext;
    const { calcSetting } = entry;
    const { imageSetting } = calcSetting;

    const [plot, setPlot] = useState();
    const [entries, setEntries] = useState([]);
    const [isGenerating, setIsGenerating] = useState(false);
    const areaIdToReportSettingsMap = useRef(new Map());
    const [entryIdToShowRadioTableMap, setEntryIdToShowRadioTableMap] = useState(new Map());

    const evaluationInput = useInput("evaluationInput");
    const planDefectInput = useInput("planDefect", []);
    const customPlanDefectInput = useInput("customPlanDefect");
    const evidenceInput = useInput("evidence");
    const entriesInput = useInput("entriesInput", [entry]);
    const [entryAreasMap, setEntryAreasMap] = useState(new Map());
    const [loadedEntriesMap, setLoadedEntriesMap] = useState(new Map());
    const [loadingEntry, setLoadingEntry] = useState(false);

    const {refreshAfterZoom, addPlan, generatePngUrl, zoomToObjects} = plotUtils;

    const loadEntries = async () => {
        const entries = await entryRest.getLazy(0, 50, entry.siteNum, "MY");
        setEntries(entries.list);
    };

    //INFO: the list of entryRest.getLazy does not reliably contain the nested calculation
    //results, which the area selection is filtered by, so a selected entry is replaced by a
    //fully loaded one. The opened entry comes from the context, it may have unsaved changes.
    const selectedEntries = entriesInput.value
        .map(e => (e.id === entry.id ? entry : loadedEntriesMap.get(e.id) ?? e));

    useEffect(() => {
        const idsToLoad = entriesInput.value
            .filter(e => e.id !== entry.id && !loadedEntriesMap.has(e.id))
            .map(e => e.id);
        if (idsToLoad.length === 0) {
            return;
        }
        (async () => {
            setLoadingEntry(true);
            try {
                const loaded = await Promise.all(idsToLoad.map(id => entryRest.getById(id)));
                setLoadedEntriesMap(prev => new Map([...prev, ...loaded.filter(e => e).map(e => [e.id, e])]));
            } finally {
                setLoadingEntry(false);
            }
        })();
    }, [entriesInput.value]);

    useEffect(() => {
        loadEntries();
        setHiddenEntryCanvasSize(900, 900);
        const p = new Plot(entryCanvasId, entry.id);
        p.addLayer(layers.plan, 10);
        p.addLayer(layers.areas, 150);
        p.addLayer(layers.result, 300);
        p.addLayer(layers.antennas, 350);
        setPlot(p);
        return () => p.canvas.dispose();
    }, []);

    const addPlans = async (entry, plot) => {
        const layer = plot.getLayer(layers.plan);
        const promises = entry.plans.map(async (plan, index) => {
            await addPlan(index, plan, layer, plot, false);
        });
        await Promise.all(promises);
        plot.canvas.renderAll();
    };

    const addAntennas = (entry, plot) => {
        const layer = plot.getLayer(layers.antennas);
        layer.removeObjects();
        const antennas = antennaUtils.loadAntennas(entry);
        for (const antenna of antennas.values()) {
            const positionX = antenna.positionX ?? 0;
            const positionY = antenna.positionY ?? 0;
            const pixelX = positionX / plot.refScale;
            const pixelY = positionY / plot.refScale * -1;
            const imageProperties = { color: operatorUtils.getColor(antenna.operator) };
            plot.addArrow(layer, antenna.index, pixelX, pixelY, antenna.direction, true, imageProperties);
        }
        plot.canvas.renderAll();
    };

    const addWattwaechterResult = async (plot, calcArea, slice, fieldMax) => {
        const calculation = calcArea.calculations.find(c => c.wattwaechterResult);
        const newImgeSetting = {...imageSetting, slice, fieldMax};
        const viewType = calcAreaUtils.isVertical(calcArea) ? ResultViewType.SIDE : ResultViewType.TOP;
        const resultData = await wattwaechterRest.getSlice(calculation.id, newImgeSetting, viewType, calcSetting.fieldType);
        const layer = plot.getLayer(layers.result);
        layer.activate();
        const pointAnalysisResult = calcArea.calculations.find(c => c.wattwaechterPointResult)?.wattwaechterPointResult;
        if(!calcAreaUtils.isVertical(calcArea)) {
            await fieldArrayResultUtils.drawTopViewResult(plot, () => null, 0, layer, calculation, calcArea, resultData, calculation.wattwaechterResult, slice, fieldMax, pointAnalysisResult, calcArea.wattwaechterCalcPoint);
        } else {
            await fieldArrayResultUtils.drawOrthogonalViewResult(plot, () => null, layer, calculation, calcArea, {}, resultData, calculation.wattwaechterResult, slice, pointAnalysisResult, calcArea.wattwaechterCalcPoint);
        }
        const { colorScaleLowerLimit, colorScaleUpperLimit } = imageSetting;
        if (!commonUtils.isBlank(colorScaleLowerLimit) && !commonUtils.isBlank(colorScaleUpperLimit)) {
            const colorBarData = await wattwaechterRest.getColorBar(calculation.id, newImgeSetting, calcSetting.fieldType);
            await plot.addColorBar(colorBarData);
        }
        plot.canvas.renderAll();
    };

    const addWorstcaseResult = async (plot, calcArea, slice) => {
        const calculation = calcArea.calculations.find(c => c.worstcaseResult);
        const viewType = calcAreaUtils.isVertical(calcArea) ? ResultViewType.SIDE : ResultViewType.TOP;
        const resultData = await worstcaseRest.getResult(calculation.id, viewType, slice);
        const layer = plot.getLayer(layers.result);
        layer.activate();
        const pointAnalysisResult = calcArea.calculations.find(c => c.worstcasePointResult)?.worstcasePointResult;
        if(!calcAreaUtils.isVertical(calcArea)) {
            await fieldArrayResultUtils.drawTopViewResult(plot, () => null, 0, layer, calculation, calcArea, resultData, calculation.worstcaseResult, slice, false, pointAnalysisResult, calcArea.worstcaseCalcPoint);
        } else {
            await fieldArrayResultUtils.drawOrthogonalViewResult(plot, () => null, layer, calculation, calcArea, {}, resultData, calculation.worstcaseResult, slice, pointAnalysisResult, calcArea.worstcaseCalcPoint);
        }
        const colorBarData = await worstcaseRest.getColorBar(calculation.id, slice);
        await plot.addColorBar(colorBarData);
        plot.canvas.renderAll();
    };

    const generateReport = async () => {
        let closeDialog = false;

        if(commonUtils.isBlank(evaluationInput.value)) {
            return;
        }

        try {
            setIsGenerating(true);

            const planLayer = plot.getLayer(layers.plan);
            const areasLayer = plot.getLayer(layers.areas);
            const resultLayer = plot.getLayer(layers.result);
            const antennasLayer = plot.getLayer(layers.antennas);
            planLayer.removeObjects();
            areasLayer.removeObjects();
            resultLayer.removeObjects();
            antennasLayer.removeObjects();

            const evaluation = evaluationInput.value;
            const planDefects = planDefectInput.value ?? [];
            const customPlanDefect = customPlanDefectInput.value ? customPlanDefectInput.value.split("\n") : [];
            const evidence = evidenceInput.value ? evidenceInput.value.split("\n") : [];
            const entryId = entry.id;

            const entryIdsToAreas = {};
            const entryIdsToShowRadioTable = {};
            for(const selectedEntry of selectedEntries) {
                entryIdsToShowRadioTable[selectedEntry.id] = entryIdToShowRadioTableMap.get(selectedEntry.id) ?? true;
                const selectedEntryAreas = entryAreasMap.get(selectedEntry.id) ?? [];
                const areas = [];
                for(const area of selectedEntryAreas) {
                    const areaId = area.id;
                    const {slice, fieldMax, worstcaseSelected, wattwaechterSelected} = areaIdToReportSettingsMap.current.get(areaId) ?? {slice: 0, fieldMax: false};

                    // prepare ww result image
                    let wattwaechterImage;
                    const wattwaechterResult = area.calculations.find(c => c.wattwaechterResult)?.wattwaechterResult;
                    if(wattwaechterResult && wattwaechterResult.numberW > slice && wattwaechterSelected) {
                        wattwaechterResult.shouldDisplay = true;
                        if(!calcAreaUtils.isVertical(area)) {
                            await addPlans(selectedEntry, plot);
                            addAntennas(selectedEntry, plot);
                        }
                        if(fieldMax) {
                            await addWattwaechterResult(plot, area, 0, fieldMax);
                        } else {
                            await addWattwaechterResult(plot, area, slice, false);
                        }
                        const imageObj = resultLayer.getObjects().find(o => o.mxwellname === fieldArrayResultUtils.names.imageName);
                        const pointAnalysis = resultLayer.getObjects()
                            .filter(o => o.mxwellname === fieldArrayResultUtils.names.operatorRatioName);
                        const antennaObjects = antennasLayer.getObjects();
                        zoomToObjects(plot, [imageObj, ...pointAnalysis, ...antennaObjects], 300);
                        await refreshAfterZoom(plot);
                        wattwaechterImage = generatePngUrl(plot);
                        planLayer.removeObjects();
                        antennasLayer.removeObjects();
                        resultLayer.removeObjects();
                    }

                    // prepare wcf result images
                    let worstcaseImage;
                    const worstcaseResult = area.calculations.find(c => c.worstcaseResult)?.worstcaseResult;
                    if(worstcaseResult && worstcaseResult.numberW > slice && worstcaseSelected) {
                        worstcaseResult.shouldDisplay = true;
                        if(!calcAreaUtils.isVertical(area)) {
                            await addPlans(selectedEntry, plot);
                            addAntennas(selectedEntry, plot);
                        }
                        await addWorstcaseResult(plot, area, slice);
                        const imageObj = resultLayer.getObjects().find(o => o.mxwellname === fieldArrayResultUtils.names.imageName);
                        const pointAnalysis = resultLayer.getObjects()
                            .filter(o => o.mxwellname === fieldArrayResultUtils.names.operatorRatioName);
                        const antennaObjects = antennasLayer.getObjects();
                        zoomToObjects(plot, [imageObj, ...pointAnalysis, ...antennaObjects], 300);
                        await refreshAfterZoom(plot);
                        worstcaseImage = generatePngUrl(plot);
                        planLayer.removeObjects();
                        antennasLayer.removeObjects();
                        resultLayer.removeObjects();
                    }

                    if(wattwaechterImage || worstcaseImage) {
                        areas.push({areaId, slice, fieldMax, wattwaechterImage, worstcaseImage});
                    }
                }
                entryIdsToAreas[selectedEntry.id] = areas;
            }

            const request = {
                entryId,
                evaluation,
                planDefects,
                customPlanDefect,
                evidence,
                entryIdsToAreas,
                entryIdsToShowRadioTable
            };

            await reportRest.getReport(request, entry);
            closeDialog = true;
        } finally {
            setIsGenerating(false);

            if(closeDialog) {
                props.onClose();
            }
        }
    };

    const onEntryAreasChange = (entryId, selectedAreas) => {
        setEntryAreasMap(prev => {
            const next = new Map(prev);
            next.set(entryId, selectedAreas);
            return next;
        });
    };

    const onAreaSettingsChange = (area, worstcaseSelected, wattwaechterSelected, slice, fieldMax) => {
        areaIdToReportSettingsMap.current.set(area.id, {worstcaseSelected, wattwaechterSelected, slice, fieldMax});
    };

    const onShowRadioTableChange = (entryId, value) => {
        setEntryIdToShowRadioTableMap(prev => {
            const next = new Map(prev);
            next.set(entryId, value);
            return next;
        });
    };


    const loading = entries.length === 0 || !plot || isGenerating || loadingEntry;
    const downloadDisabled = loading || commonUtils.isBlank(evaluationInput.value);

    return (
        <Dialog
            width="500px"
            title={intl.get("entry.report.reportButton")}
            onClose={() => props.onClose()}>
            <DialogBody>
                <div style={{minHeight: "400px"}}>
                    <Progress loading={loading}>
                        <SelectSingle {...evaluationInput.bind}
                            title={intl.get("entry.report.evaluationInput.title")}
                            toLabel={value => (value ? intl.get("entry.report.evaluationInput." + value) : "")}>
                            {Object.keys(Evaluation).map(key =>
                                <option key={key} value={Evaluation[key]} >{intl.get("entry.report.evaluationInput." + Evaluation[key])}</option>
                            )}
                        </SelectSingle>
                        <SelectMultiple
                            {...planDefectInput.bind}
                            title={intl.get("entry.report.planDefectInput.title")}
                            toLabel={value => intl.get("entry.report.planDefectInput." + value)}>
                            {Object.keys(PlanDefect).map(key =>
                                <option key={key} value={PlanDefect[key]} >{intl.get("entry.report.planDefectInput." + PlanDefect[key])}</option>
                            )}
                        </SelectMultiple>
                        <InputTextArea {...customPlanDefectInput.bind}
                            maxLength="500"
                            title={intl.get("entry.report.customPlanDefectInputTitle")}/>
                        <InputTextArea {...evidenceInput.bind}
                            maxLength="500"
                            title={intl.get("entry.report.evidenceInput")}/>
                        <SelectMultiple {...entriesInput.bind}
                            title={intl.get("entry.report.entrySelect")}
                            toLabel={e => (e ? e.name : "")}>
                            {[entry, ...entries.filter(e => e.id !== entry.id)].map(e =>
                                <option key={e.id} value={e}>{e.name}</option>
                            )}
                        </SelectMultiple>
                        {selectedEntries.map(selectedEntry => (
                            <div key={selectedEntry.id}>
                                <Spacer variant="horizontal"></Spacer>
                                <SelectMultiple
                                    value={entryAreasMap.get(selectedEntry.id) ?? []}
                                    onChange={value => onEntryAreasChange(selectedEntry.id, value)}
                                    title={`${selectedEntry.name} - ${intl.get("entry.report.areasSelect")}`}
                                    toLabel={ca => (ca ? ca.name + (ca.height ? ` ${ca.height}m` : "") : "")}>
                                    {(selectedEntry.calcSetting?.calcAreas ?? []).filter(ca => ca.name !== "OVERVIEW" && ca.calculations?.some(c => c.wattwaechterResult || c.worstcaseResult)).map(ca =>
                                        <option key={ca.id} value={ca}>{ca.name + (ca.height ? ` ${ca.height}m` : "")}</option>
                                    )}
                                </SelectMultiple>
                                {(entryAreasMap.get(selectedEntry.id) ?? [])
                                    .map(area => ({
                                        area,
                                        wattwaechterResult: area.calculations?.find(c => c.wattwaechterResult)?.wattwaechterResult,
                                        worstcaseResult: area.calculations?.find(c => c.worstcaseResult)?.worstcaseResult,
                                    }))
                                    .filter(e => e.wattwaechterResult || e.worstcaseResult)
                                    .map(e =>
                                        <div key={e.area.id}>
                                            <Spacer variant="horizontal"></Spacer>
                                            <ReportAreaItem
                                                wattwaechterResult={e.wattwaechterResult}
                                                worstcaseResult={e.worstcaseResult}
                                                area={e.area}
                                                onChange={onAreaSettingsChange}
                                            />
                                        </div>
                                    )
                                }
                                <Spacer variant="horizontal"></Spacer>
                                <Checkbox
                                    value={entryIdToShowRadioTableMap.get(selectedEntry.id) ?? true}
                                    onChange={value => onShowRadioTableChange(selectedEntry.id, value)}
                                    title={intl.get("entry.report.showRadioTable")} />
                            </div>
                        ))}

                    </Progress>
                </div>
            </DialogBody>
            <DialogFooter>
                <Button
                    svgIcon={mdiClose}
                    disabled={loading}
                    onClick={() => props.onClose()}
                    value={intl.get("common.cancel")} />
                <Spacer variant="vertical"/>
                <Button
                    svgIcon={mdiDownload}
                    disabled={downloadDisabled}
                    onClick={() => generateReport()}
                    value={intl.get("common.download")} />
            </DialogFooter>
        </Dialog>
    );
};